import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "flex flex-col justify-end pb-1.5" }
const _hoisted_4 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_5 = {
  key: 0,
  class: "text-danger bg-danger-100 p-2 text-sm shadow"
}
const _hoisted_6 = { class: "block text-sm font-medium text-gray-800" }
const _hoisted_7 = {
  controls: "",
  class: "w-full"
}
const _hoisted_8 = ["src"]

import { ref, computed, reactive, watch, onMounted, Ref } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useUiStore } from '@/stores';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';

import { required, maxLength } from '@/utils/validators';
import { toast, toastServiceError } from '@/services/core/notification';
import { getAudioFormat } from '@/utils/audio';
import { goBack } from '@/composables/useNavigation';

import { ConnectFlowAudioService, VaultService } from '@/services';
import { FormAction, ConnectFlowAudioModel, ConnectFlowAudioFormState, Permission } from '@/types';

import { UiPanel } from '@/components';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConnectFlowAudioFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const props = __props;

const route = useRoute();
const uiStore = useUiStore();

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const formState = reactive<ConnectFlowAudioFormState>({
    name: null,
    audioFilePath: null,
    isActive: true,
});

const file = ref<File | null>(null);
const audioPreview = ref<string | null>(null);

const rules = computed<ValidationArgs<ConnectFlowAudioFormState>>(() => ({
    name: { required, maxLength: maxLength(50) },
    audioFilePath: { required },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<ConnectFlowAudioFormState>>> = useVuelidate(
    rules,
    formState,
);

const fetchConnectFlowAudio = async (id: number) => {
    try {
        await setLoading(true);
        const { data } = await ConnectFlowAudioService.get<ConnectFlowAudioModel>(id);
        formState.name = data.name;
        formState.audioFilePath = data.audioFilePath;
        formState.isActive = data.isActive;
        loadAudio();
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const loadAudio = async () => {
    if (formState.audioFilePath) {
        try {
            const { data } = await VaultService.getAudioAsset(formState.audioFilePath);
            const blob = new Blob([data], { type: getAudioFormat(formState.audioFilePath) });
            file.value = new File([blob], formState.audioFilePath, {
                type: blob.type,
                lastModified: Date.now(),
            });
        } catch (e) {
            toastServiceError(e);
        }
    }
};

const onSubmit = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    await setLoading(true);

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewConnectFlowAudioById', params: { id } });
    };

    try {
        const formData = new FormData();
        formData.append('name', formState.name || '');
        formData.append('isActive', formState.isActive.toString());

        if (file.value) {
            formData.append('audio', file.value);
        }

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await ConnectFlowAudioService.update(id, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await ConnectFlowAudioService.create(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

watch([file], () => {
    if (file.value) {
        formState.audioFilePath = file.value.name;
        audioPreview.value = URL.createObjectURL(file.value);
    } else {
        formState.audioFilePath = '';
        audioPreview.value = null;
    }
});

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        await fetchConnectFlowAudio(Number(route.params.id));
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiAudioUpload = _resolveComponent("UiAudioUpload")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('connectFlowAudio.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListConnectFlowAudio' } }),
          (
                            (props.action == _unref(FormAction).CREATE &&
                                _ctx.$can(_unref(Permission).CREATE_CONNECT_FLOW_AUDIO)) ||
                            (props.action == _unref(FormAction).EDIT &&
                                _ctx.$can(_unref(Permission).EDIT_CONNECT_FLOW_AUDIO))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_unref(UiPanel), { class: "grid grid-cols-1 gap-4 md:grid-cols-3" }, {
      default: _withCtx(() => [
        _createVNode(_component_UiTextInput, {
          modelValue: _unref(v$).name.$model,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
          name: "name",
          type: "text",
          label: _ctx.$t('connectFlowAudio.labels.name'),
          required: "",
          errors: _unref(v$).name.$errors,
          onBlur: _unref(v$).name.$touch
        }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiCheckboxInput, {
            modelValue: _unref(v$).isActive.$model,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
            name: "isActive",
            label: _ctx.$t('connectFlowAudio.labels.isActive'),
            class: "h-6"
          }, null, 8, ["modelValue", "label"])
        ])
      ]),
      _: 1
    }),
    _createVNode(_unref(UiPanel), { class: "flex flex-col space-y-2" }, {
      default: _withCtx(() => [
        _createElementVNode("label", _hoisted_4, [
          _createTextVNode(_toDisplayString(_ctx.$t('connectFlowAudio.labels.audioUpload')) + " ", 1),
          _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
        ]),
        _createVNode(_component_UiAudioUpload, {
          file: file.value,
          "onUpdate:file": _cache[2] || (_cache[2] = ($event: any) => ((file).value = $event)),
          "text-rule": _ctx.$t('connectFlowAudio.create.uploadRules'),
          errors: _unref(v$).audioFilePath.$errors
        }, null, 8, ["file", "text-rule", "errors"]),
        (_unref(v$).audioFilePath.$errors && _unref(v$).audioFilePath.$errors.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).audioFilePath.$errors, (error) => {
                return (_openBlock(), _createElementBlock("p", {
                  key: error.$uid
                }, _toDisplayString(error.$message), 1))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (audioPreview.value)
      ? (_openBlock(), _createBlock(_unref(UiPanel), {
          key: 0,
          class: "space-y-2"
        }, {
          default: _withCtx(() => [
            _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t('connectFlowAudio.labels.audioPreview')), 1),
            _createElementVNode("audio", _hoisted_7, [
              _createElementVNode("source", { src: audioPreview.value }, null, 8, _hoisted_8),
              _cache[4] || (_cache[4] = _createTextVNode(" Your browser does not support the audio element. ", -1))
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})